import { useState, useRef } from 'react';
import { Plus, Pencil, Trash2, GripVertical, Eye, EyeOff, KeyRound, Link2, ImageIcon, Upload, Loader2, X, Download, ZoomIn, Server, MessageSquareText, CheckCircle2, AlertCircle } from 'lucide-react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useConfigStore } from '@/store/useConfigStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Tag';
import { generateId } from '@/lib/storage';
import { cn } from '@/lib/utils';
import { generateImage, fetchModels, type ImageJobData, type ImageModelInfo } from '@/services/imageEditService';
import { uploadImageToStorage } from '@/services/storageService';
import type { ImageEditInstance, ImageEditProvider } from '@/types';
import { IMAGE_EDIT_PROVIDERS, NANO_BANANA_MODELS } from '@/lib/constants';

// --- 选项常量 ---
const RATIO_OPTIONS = ['1:1', '3:2', '2:3', '4:3', '3:4', '5:4', '4:5', '16:9', '9:16', '2:1', '1:2', '21:9'];
const QUALITY_OPTIONS = ['standard', 'hd', 'ultra', '4k'];

function readImagePreview(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);
                return;
            }
            reject(new Error(`无法读取图片：${file.name}`));
        };
        reader.onerror = () => reject(new Error(`无法读取图片：${file.name}`));
        reader.readAsDataURL(file);
    });
}

// --- 表单状态 ---
interface FormState {
    name: string;
    provider: ImageEditProvider;
    apiUrl: string;
    apiKey: string;
    model: string;
    apiPath: string;
    defaultRatio: string;
    defaultQuality: string;
    defaultCount: number;
}

const emptyForm: FormState = {
    name: '',
    provider: 'relay',
    apiUrl: '',
    apiKey: '',
    model: '',
    apiPath: '/api/relay/image-jobs/edits',
    defaultRatio: '1:1',
    defaultQuality: 'standard',
    defaultCount: 1,
};

// --- 主页面 ---

export function ImageEditConfigPage({ embedded = false, instancesOnly = false }: { embedded?: boolean; instancesOnly?: boolean } = {}) {
    const {
        imageEditInstances,
        imageEditSettings,
        addImageEditInstance,
        updateImageEditInstance,
        deleteImageEditInstance,
        reorderImageEditInstances,
        updateImageEditSettings,
    } = useConfigStore();

    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [deleteId, setDeleteId] = useState<string | null>(null);

    const openAdd = () => {
        setForm(emptyForm);
        setEditingId(null);
        setFormOpen(true);
    };

    const openEdit = (inst: ImageEditInstance) => {
        setForm({
            name: typeof inst.name === 'string' ? inst.name : '',
            provider: inst.provider ?? 'relay',
            apiUrl: typeof inst.apiUrl === 'string' ? inst.apiUrl : '',
            apiKey: typeof inst.apiKey === 'string' ? inst.apiKey : '',
            model: typeof inst.model === 'string' ? inst.model : '',
            apiPath: typeof inst.apiPath === 'string' ? inst.apiPath : '/api/relay/image-jobs/edits',
            defaultRatio: typeof inst.defaultRatio === 'string' ? inst.defaultRatio : '1:1',
            defaultQuality: typeof inst.defaultQuality === 'string' ? inst.defaultQuality : 'standard',
            defaultCount: Math.max(1, Number(inst.defaultCount) || 1),
        });
        setEditingId(inst.id);
        setFormOpen(true);
    };

    const submit = () => {
        const name = form.name.trim();
        const apiUrl = form.apiUrl.trim();
        if (!name || !apiUrl) return;
        const data = {
            name,
            provider: form.provider,
            apiUrl,
            apiKey: form.apiKey.trim(),
            model: form.model.trim(),
            apiPath: form.provider === 'nano-banana' ? '/v1/api/generate' : (form.apiPath.trim() || '/api/relay/image-jobs/edits'),
            defaultRatio: form.defaultRatio,
            defaultQuality: form.defaultQuality,
            defaultCount: form.defaultCount,
        };
        if (editingId) {
            updateImageEditInstance(editingId, data);
        } else {
            addImageEditInstance({ id: generateId(), ...data, callEnabled: imageEditInstances.length === 0 });
        }
        setFormOpen(false);
    };

    const confirmDelete = () => {
        if (deleteId) deleteImageEditInstance(deleteId);
        setDeleteId(null);
    };

    const handleDragEnd = (e: DragEndEvent) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIndex = imageEditInstances.findIndex(i => i.id === active.id);
        const newIndex = imageEditInstances.findIndex(i => i.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        reorderImageEditInstances(arrayMove(imageEditInstances, oldIndex, newIndex));
    };

    return (
        <div className="flex h-full flex-col">
            {!embedded && <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-4 py-3">
                <div>
                    <h1 className="text-base font-semibold text-zinc-900">AI 图片编辑</h1>
                    <p className="text-xs text-zinc-500">配置图片存储、压缩、默认生成参数和预设提示词</p>
                </div>
            </div>}
            {embedded && <div className="flex shrink-0 justify-end px-4 pt-3"><Button variant="primary" size="sm" onClick={openAdd}><Plus size={14} />添加实例</Button></div>}

            <div className="flex-1 overflow-auto p-4">
                {!instancesOnly && <>
                {/* 全局设置 - 京东云 OSS 开关 */}
                <div className="mx-auto mb-4 max-w-3xl rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <h3 className="text-xs font-semibold text-zinc-600">全局设置 · 京东云 OSS 存储</h3>
                            <p className="mt-1 text-xs text-zinc-400">
                                {imageEditSettings.jdcloudStorageEnabled
                                    ? '编辑图片的修改结果将保存到当前系统京东云 OSS'
                                    : '京东云 OSS 存储已禁用，编辑图片的修改结果不会保存到京东云 OSS'}
                            </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                            {imageEditSettings.jdcloudStorageEnabled && <JdcloudStorageTestButton />}
                            <label className="flex cursor-pointer items-center gap-1.5 select-none">
                                <input
                                    type="checkbox"
                                    checked={imageEditSettings.jdcloudStorageEnabled}
                                    onChange={(e) => updateImageEditSettings({ jdcloudStorageEnabled: e.target.checked })}
                                    className="rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="text-xs text-zinc-500">
                                    {imageEditSettings.jdcloudStorageEnabled ? '已启用' : '已禁用'}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* 全局设置 - 浏览器图片压缩 */}
                <div className="mx-auto mb-4 max-w-3xl rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <h3 className="text-xs font-semibold text-zinc-600">全局设置 · 浏览器图片压缩</h3>
                            <p className="mt-1 text-xs text-zinc-400">
                                {imageEditSettings.browserCompressionEnabled
                                    ? `AI 图片保存前由当前浏览器按${imageEditSettings.compressionLevel === 'low' ? '轻度' : imageEditSettings.compressionLevel === 'high' ? '强力' : '中度'}档位压缩`
                                    : 'AI 图片保持原始文件，不在浏览器中压缩'}
                            </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                        <select value={imageEditSettings.compressionLevel} disabled={!imageEditSettings.browserCompressionEnabled} onChange={(e) => updateImageEditSettings({ compressionLevel: e.target.value as 'low' | 'medium' | 'high' })} className="h-8 rounded border border-zinc-300 bg-white px-2 text-xs disabled:bg-zinc-100">
                            <option value="low">轻度（约 600KB）</option><option value="medium">中度（约 300KB）</option><option value="high">强力（约 150KB）</option>
                        </select>
                        <label className="flex cursor-pointer items-center gap-1.5 select-none">
                            <input
                                type="checkbox"
                                checked={imageEditSettings.browserCompressionEnabled}
                                onChange={(e) => updateImageEditSettings({ browserCompressionEnabled: e.target.checked })}
                                className="rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-xs text-zinc-500">
                                {imageEditSettings.browserCompressionEnabled ? '已启用' : '已禁用'}
                            </span>
                        </label>
                        </div>
                    </div>
                </div>

                <div className="mx-auto mb-4 max-w-3xl rounded-lg border border-zinc-200 bg-white p-4">
                    <h3 className="text-xs font-semibold text-zinc-600">全局设置 · 默认生成参数</h3>
                    <p className="mt-1 text-xs text-zinc-400">自动 AI 首图优先使用这里的参数；留空时跟随各实例默认值。</p>
                    <div className="mt-3 grid grid-cols-3 gap-3">
                        <select value={imageEditSettings.defaultRatio ?? ''} onChange={(e) => updateImageEditSettings({ defaultRatio: e.target.value || undefined })} className="h-9 rounded border border-zinc-300 bg-white px-2 text-sm"><option value="">比例跟随实例</option>{RATIO_OPTIONS.map(value => <option key={value} value={value}>{value}</option>)}</select>
                        <select value={imageEditSettings.defaultQuality ?? ''} onChange={(e) => updateImageEditSettings({ defaultQuality: e.target.value || undefined })} className="h-9 rounded border border-zinc-300 bg-white px-2 text-sm"><option value="">质量跟随实例</option>{QUALITY_OPTIONS.map(value => <option key={value} value={value}>{value}</option>)}</select>
                        <select value={imageEditSettings.defaultCount ?? ''} onChange={(e) => updateImageEditSettings({ defaultCount: e.target.value ? Number(e.target.value) : undefined })} className="h-9 rounded border border-zinc-300 bg-white px-2 text-sm"><option value="">数量跟随实例</option>{[1, 2, 3, 4].map(value => <option key={value} value={value}>{value} 张</option>)}</select>
                    </div>
                </div>

                {/* 全局设置 - 预设提示词 */}
                <div className="mx-auto mb-4 max-w-3xl rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-xs font-semibold text-zinc-600">全局设置 · 预设提示词</h3>
                        <Button variant="outline" size="sm" onClick={() => {
                            const current = imageEditSettings.presetPrompts ?? [];
                            updateImageEditSettings({ presetPrompts: [...current, ''] });
                        }}>
                            <Plus size={13} />
                            添加
                        </Button>
                    </div>
                    {(imageEditSettings.presetPrompts ?? []).length === 0 ? (
                        <p className="text-xs text-zinc-400">暂无预设提示词，点击"添加"创建</p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {(imageEditSettings.presetPrompts ?? []).map((p, i) => (
                                <div key={i} className="flex items-start gap-2">
                                    <span className="mt-2 shrink-0 text-[10px] font-mono text-zinc-400">{i + 1}</span>
                                    <textarea
                                        value={p}
                                        onChange={(e) => {
                                            const newList = [...(imageEditSettings.presetPrompts ?? [])];
                                            newList[i] = e.target.value;
                                            updateImageEditSettings({ presetPrompts: newList });
                                        }}
                                        rows={2}
                                        className="min-h-[60px] w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400 resize-none"
                                        placeholder="输入提示词…"
                                    />
                                    <button
                                        onClick={() => {
                                            const newList = (imageEditSettings.presetPrompts ?? []).filter((_, idx) => idx !== i);
                                            updateImageEditSettings({ presetPrompts: newList });
                                        }}
                                        className="mt-1.5 shrink-0 rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-500"
                                        title="删除"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                </>}

                {instancesOnly && (imageEditInstances.length === 0 ? (
                    <EmptyState onAdd={openAdd} />
                ) : (
                    <div className="mx-auto max-w-3xl">
                        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext
                                items={imageEditInstances.map(i => i.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="flex flex-col gap-3">
                                    {imageEditInstances.map(inst => (
                                        <SortableCard
                                            key={inst.id}
                                            instance={inst}
                                            onToggleEnabled={() => updateImageEditInstance(inst.id, { callEnabled: inst.callEnabled !== true })}
                                            onEdit={() => openEdit(inst)}
                                            onDelete={() => setDeleteId(inst.id)}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    </div>
                ))}
            </div>

            <Modal
                open={formOpen}
                onClose={() => setFormOpen(false)}
                title={editingId ? '编辑实例' : '添加实例'}
                width="md"
            >
                <ImageEditInstanceForm
                    form={form}
                    onChange={setForm}
                    onSubmit={submit}
                    onCancel={() => setFormOpen(false)}
                />
            </Modal>

            <ConfirmDialog
                open={!!deleteId}
                title="删除实例"
                message="确定要删除该 AI 图片编辑实例吗？此操作不可撤销。"
                onConfirm={confirmDelete}
                onCancel={() => setDeleteId(null)}
            />
        </div>
    );
}

function JdcloudStorageTestButton() {
    const [testing, setTesting] = useState(false);
    const [result, setResult] = useState<'success' | 'error' | null>(null);
    const [message, setMessage] = useState('');
    const [uploadedUrl, setUploadedUrl] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setTesting(true);
        setResult(null);
        setMessage('');
        setUploadedUrl('');

        try {
            const fileBase64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve((reader.result as string).replace(/^data:.*;base64,/, ''));
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const url = await uploadImageToStorage({
                fileName: file.name,
                fileBase64,
                contentType: file.type || 'image/png',
            });

            setUploadedUrl(url);
            setResult('success');
            setMessage('上传成功');
        } catch (error) {
            setResult('error');
            setMessage(error instanceof Error ? error.message : '上传失败');
        } finally {
            setTesting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div className="flex items-center gap-2">
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
            />
            <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={testing}
            >
                {testing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {testing ? '上传中…' : '测试上传'}
            </Button>
            {result === 'success' && (
                <a
                    href={uploadedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-emerald-600 hover:underline"
                    title={uploadedUrl}
                >
                    <CheckCircle2 size={13} /> 上传成功
                </a>
            )}
            {result === 'error' && (
                <span className="flex max-w-64 items-center gap-1 truncate text-xs text-red-600" title={message}>
                    <AlertCircle size={13} className="shrink-0" /> {message || '上传失败'}
                </span>
            )}
        </div>
    );
}

// --- 可排序卡片 ---

function SortableCard({
    instance,
    onToggleEnabled,
    onEdit,
    onDelete,
}: {
    instance: ImageEditInstance;
    onToggleEnabled: () => void;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: instance.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    const maskedKey = instance.apiKey
        ? instance.apiKey.length > 4
            ? `••••••••${instance.apiKey.slice(-4)}`
            : '••••••••'
        : '未设置';

    // 测试功能状态
    const [testOpen, setTestOpen] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testPrompt, setTestPrompt] = useState('');
    const [testImages, setTestImages] = useState<File[]>([]);
    const [testRatio, setTestRatio] = useState(instance.defaultRatio);
    const [testQuality, setTestQuality] = useState(instance.defaultQuality);
    const [testCount, setTestCount] = useState(instance.defaultCount);
    const [jobStatus, setJobStatus] = useState<string>('');
    const [testResult, setTestResult] = useState<ImageJobData | null>(null);
    const [testError, setTestError] = useState<string | null>(null);
    const [elapsedMs, setElapsedMs] = useState<number>(0);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [refImageUrls, setRefImageUrls] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleTest = async () => {
        if (!testPrompt.trim()) return;
        setTesting(true);
        setTestError(null);
        setTestResult(null);
        setJobStatus('提交中');
        setElapsedMs(0);
        const startTime = Date.now();
        try {
            const result = await generateImage(
                instance,
                {
                    prompt: testPrompt,
                    images: testImages.length > 0 ? testImages : undefined,
                    ratio: testRatio,
                    quality: testQuality,
                    count: testCount,
                },
                (status) => {
                    const label = status === 'queued' ? '排队中' : status === 'running' ? '生成中' : status;
                    setJobStatus(label);
                    setElapsedMs(Date.now() - startTime);
                },
            );
            setElapsedMs(Date.now() - startTime);
            setTestResult(result);
        } catch (err) {
            setElapsedMs(Date.now() - startTime);
            setTestError(err instanceof Error ? err.message : String(err));
        } finally {
            setTesting(false);
            setJobStatus('');
        }
    };

    const handleCancelTest = () => {
        setTesting(false);
        setJobStatus('');
    };

    // 获取图片 src：处理相对 URL
    const getImageSrc = (item: { url?: string; b64_json?: string }): string | null => {
        if (item.b64_json) return `data:image/png;base64,${item.b64_json}`;
        if (item.url) {
            // 相对路径需要拼接 base URL
            if (item.url.startsWith('/')) {
                return `${instance.apiUrl.replace(/\/$/, '')}${item.url}`;
            }
            return item.url;
        }
        return null;
    };

    // 从结果中提取所有图片
    const resultImages = testResult?.result?.data ?? testResult?.result?.images ?? [];

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                'rounded-lg border border-zinc-200 bg-white shadow-sm',
                isDragging && 'opacity-70 shadow-md ring-2 ring-zinc-300',
            )}
        >
            {/* 基本信息 */}
            <div className="flex items-start gap-3 p-4">
                <button
                    {...attributes}
                    {...listeners}
                    className="mt-0.5 cursor-grab touch-none rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 active:cursor-grabbing"
                    title="拖拽排序"
                >
                    <GripVertical size={18} />
                </button>

                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-sm font-semibold text-zinc-900">{instance.name}</h3>
                        <Badge variant="default">{instance.provider === 'nano-banana' ? 'NanoBanana' : 'Relay'}</Badge>
                        <Badge variant="default">{instance.model}</Badge>
                    </div>
                    <div className="mt-2 flex flex-col gap-1.5 text-xs text-zinc-500">
                        <div className="flex items-center gap-1.5">
                            <Link2 size={13} className="shrink-0 text-zinc-400" />
                            <span className="truncate" title={instance.apiUrl}>{instance.apiUrl}</span>
                            <span className="text-zinc-300">|</span>
                            <span className="font-mono text-zinc-400">{instance.apiPath}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <KeyRound size={13} className="shrink-0 text-zinc-400" />
                            <span className="font-mono">{maskedKey}</span>
                        </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-zinc-400">
                        <span>{instance.defaultRatio}</span>
                        <span className="text-zinc-200">|</span>
                        <span>{instance.defaultQuality}</span>
                        <span className="text-zinc-200">|</span>
                        <span>x{instance.defaultCount}</span>
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                    <label className="mr-2 flex cursor-pointer items-center gap-1.5 text-xs text-zinc-500" title="启用后按当前排序参与自动调用，失败时切换到下一个实例">
                        <input
                            type="checkbox"
                            checked={instance.callEnabled === true}
                            onChange={onToggleEnabled}
                            className="rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        自动调用
                    </label>
                    <Button variant="ghost" size="sm" onClick={() => setTestOpen(v => !v)}>
                        <ImageIcon size={14} />
                        测试
                    </Button>
                    <Button variant="ghost" size="sm" onClick={onEdit}>
                        <Pencil size={14} />
                        编辑
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onDelete}
                        className="text-red-500 hover:bg-red-50 hover:text-red-600"
                    >
                        <Trash2 size={14} />
                        删除
                    </Button>
                </div>
            </div>

            {/* 测试面板 - 可折叠 */}
            {testOpen && (
                <div className="border-t border-zinc-100 bg-zinc-50/50 p-4">
                    <div className="flex flex-col gap-3">
                        {/* Prompt 输入 */}
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-zinc-600">Prompt</label>
                            <textarea
                                value={testPrompt}
                                onChange={(e) => setTestPrompt(e.target.value)}
                                placeholder="输入图片描述，如：保持主体不变，更换为白色背景"
                                rows={2}
                                className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400 resize-none"
                            />
                        </div>

                        {/* 参考图上传（可选） */}
                        <div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                onChange={async (e) => {
                                    const files = Array.from(e.target.files ?? []);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                    if (files.length === 0) return;

                                    try {
                                        const newUrls = await Promise.all(files.map(readImagePreview));
                                        setTestImages(prev => [...prev, ...files]);
                                        setRefImageUrls(prev => [...prev, ...newUrls]);
                                        setTestError(null);
                                    } catch (error) {
                                        setTestError(error instanceof Error ? error.message : '参考图读取失败');
                                    }
                                }}
                            />
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                                    <Upload size={13} />
                                    上传参考图（可选）
                                </Button>
                                {testImages.length > 0 && (
                                    <span className="text-xs text-zinc-500">已选 {testImages.length} 张</span>
                                )}
                            </div>
                            {testImages.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {testImages.map((file, i) => (
                                        <div key={i} className="flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1">
                                            <span className="max-w-[120px] truncate text-xs text-zinc-600">{file.name}</span>
                                            <button onClick={() => {
                                                setTestImages(prev => prev.filter((_, idx) => idx !== i));
                                                setRefImageUrls(prev => prev.filter((_, idx) => idx !== i));
                                            }} className="text-zinc-400 hover:text-zinc-600">
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 参数 */}
                        <div className="flex flex-wrap items-end gap-3">
                            <div className="flex flex-col gap-1">
                                <label className="text-[11px] text-zinc-500">比例</label>
                                <select
                                    value={testRatio}
                                    onChange={(e) => setTestRatio(e.target.value)}
                                    className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-900"
                                >
                                    {RATIO_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-[11px] text-zinc-500">质量</label>
                                <select
                                    value={testQuality}
                                    onChange={(e) => setTestQuality(e.target.value)}
                                    className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-900"
                                >
                                    {QUALITY_OPTIONS.map(q => <option key={q} value={q}>{q}</option>)}
                                </select>
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-[11px] text-zinc-500">数量</label>
                                <input
                                    type="number"
                                    min={1}
                                    max={50}
                                    value={testCount}
                                    onChange={(e) => setTestCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                                    className="h-8 w-16 rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-900"
                                />
                            </div>
                        </div>

                        {/* 执行按钮 */}
                        <div className="flex items-center gap-3">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleTest}
                                disabled={testing || !testPrompt.trim()}
                            >
                                {testing ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                                {testing ? (jobStatus || '处理中...') : '生成图片'}
                            </Button>
                            {testing && (
                                <Button variant="outline" size="sm" onClick={handleCancelTest}>
                                    取消
                                </Button>
                            )}
                            {testing && elapsedMs > 0 && (
                                <span className="text-xs text-zinc-400">{(elapsedMs / 1000).toFixed(1)}s</span>
                            )}
                        </div>

                        {/* 错误信息 */}
                        {testError && (
                            <div className="max-h-48 overflow-auto rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 whitespace-pre-wrap">
                                {testError}
                            </div>
                        )}

                        {/* 对比展示 */}
                        {(refImageUrls.length > 0 || resultImages.length > 0) && (
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-zinc-600">对比结果</span>
                                    {testResult?.result?.actual_size && (
                                        <span className="text-[11px] text-zinc-400">{testResult.result.actual_size}</span>
                                    )}
                                    {elapsedMs > 0 && (
                                        <span className="text-[11px] text-zinc-400">耗时 {(elapsedMs / 1000).toFixed(1)}s</span>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    {/* 原始参考图 */}
                                    {refImageUrls.length > 0 && (
                                        <div className="flex flex-col gap-1.5">
                                            <span className="text-[11px] font-medium text-zinc-400">原始图片</span>
                                            <div className="flex flex-wrap gap-2">
                                                {refImageUrls.map((url, i) => (
                                                    <div
                                                        key={`ref-${i}`}
                                                        className="group relative cursor-pointer overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm transition-transform hover:scale-[1.02]"
                                                        onClick={() => setPreviewUrl(url)}
                                                    >
                                                        <img
                                                            src={url}
                                                            alt={`参考图 ${i + 1}`}
                                                            className="h-36 w-36 object-cover"
                                                        />
                                                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all group-hover:bg-black/10 group-hover:opacity-100">
                                                            <ZoomIn size={20} className="text-white drop-shadow" />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {/* 生成结果图 */}
                                    {resultImages.length > 0 && (
                                        <div className="flex flex-col gap-1.5">
                                            <span className="text-[11px] font-medium text-zinc-400">生成结果</span>
                                            <div className="flex flex-wrap gap-2">
                                                {resultImages.map((item, i) => {
                                                    const src = getImageSrc(item);
                                                    if (!src) return null;
                                                    return (
                                                        <div
                                                            key={`gen-${i}`}
                                                            className="group relative cursor-pointer overflow-hidden rounded-lg border border-emerald-200 bg-white shadow-sm transition-transform hover:scale-[1.02]"
                                                            onClick={() => setPreviewUrl(src)}
                                                        >
                                                            <img
                                                                src={src}
                                                                alt={`生成结果 ${i + 1}`}
                                                                className="h-36 w-36 object-cover"
                                                            />
                                                            <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all group-hover:bg-black/10 group-hover:opacity-100">
                                                                <ZoomIn size={20} className="text-white drop-shadow" />
                                                            </div>
                                                            <a
                                                                href={src}
                                                                download={`generated_${i + 1}.png`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-zinc-600 opacity-0 shadow hover:bg-white group-hover:opacity-100"
                                                            >
                                                                <Download size={13} />
                                                            </a>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 大图预览 */}
            {previewUrl && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70" onClick={() => setPreviewUrl(null)}>
                    <div className="relative max-h-[90vh] max-w-[90vw]">
                        <img src={previewUrl} alt="" className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
                        <button onClick={() => setPreviewUrl(null)} className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-lg hover:bg-zinc-100"><X size={14} className="text-zinc-600" /></button>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- 实例表单 ---

function ImageEditInstanceForm({
    form,
    onChange,
    onSubmit,
    onCancel,
}: {
    form: FormState;
    onChange: (f: FormState) => void;
    onSubmit: () => void;
    onCancel: () => void;
}) {
    const [showKey, setShowKey] = useState(false);
    const update = (patch: Partial<FormState>) => onChange({ ...form, ...patch });
    const valid = form.name.trim() !== '' && form.apiUrl.trim() !== '' && form.model.trim() !== '';

    return (
        <div className="flex flex-col gap-4">
            <Input
                label="名称"
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="如：Luchi 图生图"
            />
            <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-zinc-600">接口类型</label>
                <select value={form.provider} onChange={(e) => {
                    const provider = e.target.value as ImageEditProvider;
                    update(provider === 'nano-banana'
                        ? { provider, apiUrl: 'https://grsaiapi.com', apiPath: '/v1/api/generate', model: 'nano-banana-2' }
                        : { provider, apiUrl: 'https://image.luchikey.com', apiPath: '/api/relay/image-jobs/edits', model: 'gpt-image-2' });
                }} className="h-9 rounded-md border border-zinc-300 bg-white px-3 text-sm">
                    {IMAGE_EDIT_PROVIDERS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <p className="text-[11px] text-zinc-400">{IMAGE_EDIT_PROVIDERS.find(option => option.value === form.provider)?.desc}</p>
            </div>
            <Input
                label="API Base URL"
                value={form.apiUrl}
                onChange={(e) => update({ apiUrl: e.target.value })}
                placeholder="https://image.luchikey.com"
            />
            {form.provider === 'relay' && <Input
                label="接口路径"
                value={form.apiPath}
                onChange={(e) => update({ apiPath: e.target.value })}
                placeholder="/api/relay/image-jobs/edits"
            />}
            <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-zinc-600">API Key</label>
                <div className="relative">
                    <input
                        type={showKey ? 'text' : 'password'}
                        value={form.apiKey}
                        onChange={(e) => update({ apiKey: e.target.value })}
                        placeholder="sk-..."
                        className="h-9 w-full rounded-md border border-zinc-300 bg-white px-3 pr-9 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                    />
                    <button
                        type="button"
                        onClick={() => setShowKey(v => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                        title={showKey ? '隐藏' : '显示'}
                    >
                        {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                </div>
            </div>
            {form.provider === 'nano-banana' ? <div className="flex flex-col gap-1"><label className="text-xs font-medium text-zinc-600">模型</label><select value={form.model} onChange={(e) => update({ model: e.target.value })} className="h-9 rounded-md border border-zinc-300 bg-white px-3 text-sm">{NANO_BANANA_MODELS.map(model => <option key={model} value={model}>{model}</option>)}</select></div> : <Input
                label="模型"
                value={form.model}
                onChange={(e) => update({ model: e.target.value })}
                placeholder="gpt-image-2"
            />}

            {/* 默认参数 */}
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                <h4 className="mb-3 text-xs font-semibold text-zinc-600">默认生成参数</h4>
                <div className="grid grid-cols-3 gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-[11px] text-zinc-500">比例</label>
                        <select
                            value={form.defaultRatio}
                            onChange={(e) => update({ defaultRatio: e.target.value })}
                            className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-900 focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                        >
                            {RATIO_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-[11px] text-zinc-500">质量</label>
                        <select
                            value={form.defaultQuality}
                            onChange={(e) => update({ defaultQuality: e.target.value })}
                            className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-900 focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                        >
                            {QUALITY_OPTIONS.map(q => <option key={q} value={q}>{q}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-[11px] text-zinc-500">数量 (1-50)</label>
                        <input
                            type="number"
                            min={1}
                            max={50}
                            value={form.defaultCount}
                            onChange={(e) => update({ defaultCount: Math.max(1, Math.min(50, Number(e.target.value) || 1)) })}
                            className="h-8 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-900 focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                        />
                    </div>
                </div>
            </div>

            <div className="mt-2 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={onCancel}>取消</Button>
                <Button variant="primary" size="sm" onClick={onSubmit} disabled={!valid}>
                    保存
                </Button>
            </div>
        </div>
    );
}

// --- 空状态 ---

function EmptyState({ onAdd }: { onAdd: () => void }) {
    return (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-zinc-100 text-zinc-400">
                <ImageIcon size={28} />
            </div>
            <div>
                <p className="text-sm font-medium text-zinc-700">暂无 AI 图片编辑实例</p>
                <p className="text-xs text-zinc-400">添加一个实例以开始使用图生图功能</p>
            </div>
            <Button variant="primary" size="sm" onClick={onAdd}>
                <Plus size={14} />
                添加实例
            </Button>
        </div>
    );
}
